import { useEffect, useState, useRef, useCallback } from 'react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import {
  Image, StickyNote, Trash2, X, Check, ChevronDown,
  ArrowUpRight, Square, Circle, ZoomIn, ZoomOut,
  Plus, Download,
} from 'lucide-react'

const NOTE_COLORS = [
  '#fef08a', '#fca5a5', '#86efac', '#93c5fd', '#d8b4fe', '#fdba74', '#f0f0f0',
]
const STROKE_COLORS = [
  '#a78bfa', '#60a5fa', '#34d399', '#fb923c', '#f472b6', '#f87171', '#ffffff',
]

/* ─── NoteModal ─── */
const NoteModal = ({ onClose, onConfirm }) => {
  const [testo, setTesto] = useState('')
  const [colore, setColore] = useState('#fef08a')
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Nuova nota</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <textarea autoFocus rows={4} value={testo} onChange={e => setTesto(e.target.value)}
          placeholder="Scrivi la tua nota..."
          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 resize-none mb-4" />
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2">Colore</p>
          <div className="flex gap-2 flex-wrap">
            {NOTE_COLORS.map(c => (
              <button key={c} onClick={() => setColore(c)}
                className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 flex items-center justify-center"
                style={{ backgroundColor: c, borderColor: colore === c ? '#7c3aed' : 'transparent' }}>
                {colore === c && <Check size={12} className="text-gray-800" />}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:text-white text-sm transition-colors">Annulla</button>
          <button onClick={() => { if (testo.trim()) onConfirm(testo.trim(), colore) }}
            disabled={!testo.trim()}
            className="flex-1 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium transition-colors">
            Aggiungi
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Arrow SVG ─── */
const ArrowItem = ({ item, isOwner, hovered, selected, onMouseEnter, onMouseLeave, onMouseDown, onDelete, onSelect, zoom }) => {
  const x1 = item.pos_x * zoom
  const y1 = item.pos_y * zoom
  const x2 = (item.end_x ?? item.pos_x + 120) * zoom
  const y2 = (item.end_y ?? item.pos_y) * zoom
  const pad = 24 * zoom
  const minX = Math.min(x1, x2) - pad
  const minY = Math.min(y1, y2) - pad
  const maxX = Math.max(x1, x2) + pad
  const maxY = Math.max(y1, y2) + pad
  const w = maxX - minX
  const h = maxY - minY
  const lx1 = x1 - minX
  const ly1 = y1 - minY
  const lx2 = x2 - minX
  const ly2 = y2 - minY
  const color = item.stroke_color || '#a78bfa'
  const strokeW = Math.max(1.5, 2 * zoom)
  const showDelete = isOwner && (hovered || selected)

  return (
    <div className="absolute" style={{ left: minX, top: minY, width: w, height: h, pointerEvents: 'none' }}>
      <svg width={w} height={h} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'all', cursor: 'pointer' }}
        onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
        onMouseDown={e => onMouseDown(e, item)}
        onClick={e => { e.stopPropagation(); onSelect(item.id) }}>
        <defs>
          <marker id={`arrow-${item.id}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill={selected ? '#ffffff' : color} />
          </marker>
          {selected && (
            <filter id={`glow-${item.id}`}>
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          )}
        </defs>
        {/* Selection glow */}
        {selected && (
          <line x1={lx1} y1={ly1} x2={lx2} y2={ly2}
            stroke="rgba(255,255,255,0.25)" strokeWidth={strokeW + 6}
            strokeLinecap="round"
          />
        )}
        <line x1={lx1} y1={ly1} x2={lx2} y2={ly2}
          stroke={selected ? '#ffffff' : color}
          strokeWidth={selected ? strokeW + 1 : strokeW}
          markerEnd={`url(#arrow-${item.id})`}
          strokeLinecap="round"
          opacity={hovered || selected ? 1 : 0.85}
        />
        {/* Wide transparent hit area — makes clicking easy */}
        <line x1={lx1} y1={ly1} x2={lx2} y2={ly2}
          stroke="transparent" strokeWidth={Math.max(20, 22 * zoom)} />
      </svg>
      {showDelete && (
        <button onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onDelete(item.id) }}
          style={{ position: 'absolute', left: (lx1 + lx2) / 2 - 14, top: (ly1 + ly2) / 2 - 14, pointerEvents: 'all' }}
          className="w-7 h-7 bg-red-600/80 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg z-10">
          <Trash2 size={12} className="text-white" />
        </button>
      )}
    </div>
  )
}

/* ─── Shape (Rect/Circle) ─── */
const ShapeItem = ({ item, isOwner, hovered, selected, onMouseEnter, onMouseLeave, onMouseDown, onTouchStart, onDelete, onSelect, zoom }) => {
  const w = (item.width || 160) * zoom
  const h = (item.height || 100) * zoom
  const color = item.stroke_color || '#a78bfa'
  const isCircle = item.type === 'circle'
  const showDelete = isOwner && (hovered || selected)
  return (
    <div className="absolute group select-none"
      style={{ left: item.pos_x * zoom, top: item.pos_y * zoom, width: w, height: h, cursor: 'grab' }}
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      onMouseDown={e => onMouseDown(e, item)}
      onTouchStart={e => { e.stopPropagation(); onTouchStart(e, item) }}
      onClick={e => { e.stopPropagation(); onSelect(item.id) }}>
      <div className="w-full h-full transition-all"
        style={{
          border: `${Math.max(1.5, 2 * zoom)}px solid ${selected ? '#ffffff' : color}`,
          borderRadius: isCircle ? '50%' : `${6 * zoom}px`,
          opacity: hovered || selected ? 1 : 0.8,
          backgroundColor: selected ? `${color}25` : `${color}10`,
          boxShadow: selected ? `0 0 0 2px rgba(255,255,255,0.2), 0 0 12px ${color}50` : 'none',
        }} />
      {showDelete && (
        <button onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onDelete(item.id) }}
          className="absolute -top-3 -right-3 w-6 h-6 bg-red-600/80 hover:bg-red-600 rounded-full flex items-center justify-center shadow z-10">
          <Trash2 size={11} className="text-white" />
        </button>
      )}
    </div>
  )
}

/* ─── Image Item ─── */
const handleImgDownload = async (url, e) => {
  e.stopPropagation()
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() || 'jpg'
  const filename = `moodboard_${Date.now()}.${ext}`
  try {
    const response = await fetch(url, { mode: 'cors', cache: 'no-store' })
    if (!response.ok) throw new Error('fetch failed')
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(blobUrl), 2000)
  } catch {
    /* Fallback: open in new tab (user can save manually) */
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}

const ImageItem = ({ item, isOwner, hovered, selected, onMouseEnter, onMouseLeave, onMouseDown, onTouchStart, onDelete, onSelect, onResizeStart, zoom }) => {
  const w = item.width * zoom
  const showControls = hovered || selected
  return (
    <div className="absolute group select-none"
      style={{ left: item.pos_x * zoom, top: item.pos_y * zoom, width: w, cursor: 'grab' }}
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      onMouseDown={e => onMouseDown(e, item)}
      onTouchStart={e => { e.stopPropagation(); onTouchStart(e, item) }}
      onClick={e => { e.stopPropagation(); onSelect(item.id) }}>
      {/* Image */}
      <div className={`relative rounded-xl overflow-hidden shadow-xl border-2 transition-colors ${selected ? 'border-white shadow-[0_0_12px_rgba(255,255,255,0.3)]' : hovered ? 'border-violet-500' : 'border-transparent'}`}>
        <img src={item.image_url} alt="" className="w-full h-auto block pointer-events-none" draggable={false} />
      </div>
      {/* Action buttons — outside overflow:hidden so they always render correctly */}
      <div className={`absolute top-2 right-2 flex items-center gap-1.5 transition-all duration-150 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <button
          onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}
          onClick={e => handleImgDownload(item.image_url, e)}
          title="Scarica"
          className="w-7 h-7 bg-black/70 hover:bg-violet-600 rounded-full flex items-center justify-center shadow-lg">
          <Download size={13} className="text-white" />
        </button>
        {isOwner && (
          <button
            onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); onDelete(item.id) }}
            title="Elimina"
            className="w-7 h-7 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg">
            <Trash2 size={13} className="text-white" />
          </button>
        )}
      </div>
      {/* Resize handle */}
      {isOwner && (hovered || selected) && (
        <div
          onMouseDown={e => { e.stopPropagation(); onResizeStart(e, item) }}
          className="absolute -bottom-2 -right-2 w-5 h-5 bg-violet-500 border-2 border-white rounded-full cursor-se-resize hover:bg-violet-400 transition-colors shadow-lg z-10"
          title="Ridimensiona"
        />
      )}
    </div>
  )
}

/* ─── Note Item ─── */
const NoteItem = ({ item, isOwner, hovered, selected, onMouseEnter, onMouseLeave, onMouseDown, onTouchStart, onDelete, onSelect, zoom }) => {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(item.content || '')
  const w = item.width * zoom

  const handleSave = async () => {
    setEditing(false)
    if (text.trim() === item.content) return
    await supabase.from('moodboard_items').update({ content: text.trim() }).eq('id', item.id)
  }

  return (
    <div className="absolute group select-none"
      style={{ left: item.pos_x * zoom, top: item.pos_y * zoom, width: w, cursor: editing ? 'default' : 'grab' }}
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      onMouseDown={e => { if (!editing) onMouseDown(e, item) }}
      onTouchStart={e => { if (!editing) { e.stopPropagation(); onTouchStart(e, item) } }}
      onClick={e => { if (!editing) { e.stopPropagation(); onSelect(item.id) } }}>
      <div className={`rounded-xl shadow-xl border-2 p-4 transition-colors ${selected ? 'border-gray-600 shadow-[0_0_10px_rgba(0,0,0,0.4)]' : hovered && !editing ? 'border-violet-500' : 'border-transparent'}`}
        style={{ backgroundColor: item.color || '#fef08a' }}>
        {editing ? (
          <textarea autoFocus value={text} onChange={e => setText(e.target.value)}
            onBlur={handleSave}
            onKeyDown={e => { if (e.key === 'Escape') { setEditing(false); setText(item.content || '') } }}
            className="w-full bg-transparent text-gray-900 text-sm font-medium resize-none focus:outline-none leading-relaxed"
            rows={Math.max(3, text.split('\n').length)} style={{ minHeight: '60px', fontSize: `${13 * zoom}px` }} />
        ) : (
          <p className="text-gray-900 font-medium leading-relaxed whitespace-pre-wrap cursor-text"
            style={{ minHeight: '40px', fontSize: `${13 * zoom}px` }}
            onDoubleClick={() => { if (isOwner) setEditing(true) }}>
            {item.content || ''}
          </p>
        )}
        {!editing && isOwner && (
          <div className="flex justify-end gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onClick={() => setEditing(true)}
              className="w-6 h-6 bg-black/10 hover:bg-black/20 rounded-lg flex items-center justify-center">
              <Plus size={11} className="text-gray-800" />
            </button>
            <button onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()} onClick={() => onDelete(item.id)}
              className="w-6 h-6 bg-black/10 hover:bg-red-500 rounded-lg flex items-center justify-center">
              <Trash2 size={11} className="text-gray-800" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Main ─── */
const CANVAS_W = 3200
const CANVAS_H = 2200

const Moodboard = () => {
  const { profile } = useAuth()
  const myId = profile?.id

  const [progetti, setProgetti] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [showProjectPicker, setShowProjectPicker] = useState(false)
  const [items, setItems] = useState([])
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [uploading, setUploading] = useState(0)
  const [hoveredId, setHoveredId] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [zoom, setZoom] = useState(0.75)
  const [toolMode, setToolMode] = useState('select') // 'select' | 'arrow' | 'rect' | 'circle'
  const [strokeColor, setStrokeColor] = useState('#a78bfa')
  const [drawStart, setDrawStart] = useState(null)
  const [drawPreview, setDrawPreview] = useState(null)

  const canvasWrapRef = useRef(null)
  const fileInputRef = useRef(null)
  const channelRef = useRef(null)
  const dragRef = useRef(null)
  const resizeRef = useRef(null)
  const undoStackRef = useRef([])

  /* fetch progetti — solo quelli in cui partecipo */
  useEffect(() => {
    if (!myId) return
    supabase.from('progetti')
      .select('id, titolo, colore, project_members!inner(user_id)')
      .eq('project_members.user_id', myId)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setProgetti(data || [])
        if (data?.length > 0) setSelectedProject(data[0])
      })
  }, [myId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* fetch items */
  const fetchItems = useCallback(async () => {
    if (!selectedProject) return
    const { data } = await supabase.from('moodboard_items').select('*')
      .eq('project_id', selectedProject.id).order('created_at', { ascending: true })
    setItems(data || [])
  }, [selectedProject])

  useEffect(() => { fetchItems() }, [fetchItems])

  /* realtime */
  useEffect(() => {
    if (!selectedProject) return
    if (channelRef.current) supabase.removeChannel(channelRef.current)
    const ch = supabase.channel(`moodboard-${selectedProject.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'moodboard_items', filter: `project_id=eq.${selectedProject.id}` },
        payload => setItems(prev => prev.find(i => i.id === payload.new.id) ? prev : [...prev, payload.new]))
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'moodboard_items', filter: `project_id=eq.${selectedProject.id}` },
        payload => setItems(prev => prev.map(i => i.id === payload.new.id ? payload.new : i)))
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'moodboard_items', filter: `project_id=eq.${selectedProject.id}` },
        payload => setItems(prev => prev.filter(i => i.id !== payload.old.id)))
      .subscribe()
    channelRef.current = ch
    return () => { supabase.removeChannel(ch) }
  }, [selectedProject])

  /* canvas coordinate helper */
  const toCanvas = useCallback((clientX, clientY) => {
    const rect = canvasWrapRef.current?.getBoundingClientRect()
    if (!rect) return { x: 0, y: 0 }
    const scrollLeft = canvasWrapRef.current.scrollLeft
    const scrollTop = canvasWrapRef.current.scrollTop
    return {
      x: Math.round((clientX - rect.left + scrollLeft) / zoom),
      y: Math.round((clientY - rect.top + scrollTop) / zoom),
    }
  }, [zoom])

  /* ─── Drag ─── */
  const handleMouseDownItem = useCallback((e, item) => {
    if (e.button !== 0 || toolMode !== 'select') return
    e.preventDefault(); e.stopPropagation()
    dragRef.current = { id: item.id, startMouseX: e.clientX, startMouseY: e.clientY, origX: item.pos_x, origY: item.pos_y }
    const onMove = (ev) => {
      if (!dragRef.current) return
      const { id, startMouseX, startMouseY, origX, origY } = dragRef.current
      const newX = Math.max(0, origX + (ev.clientX - startMouseX) / zoom)
      const newY = Math.max(0, origY + (ev.clientY - startMouseY) / zoom)
      setItems(prev => prev.map(i => i.id === id ? { ...i, pos_x: newX, pos_y: newY } : i))
    }
    const onUp = () => {
      if (!dragRef.current) return
      const { id } = dragRef.current
      setItems(prev => {
        const it = prev.find(i => i.id === id)
        if (it) supabase.from('moodboard_items').update({ pos_x: Math.round(it.pos_x), pos_y: Math.round(it.pos_y) }).eq('id', id).then(() => {})
        return prev
      })
      dragRef.current = null
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [toolMode, zoom])

  /* ─── Touch Drag (mobile) ─── */
  const handleTouchStartItem = useCallback((e, item) => {
    if (toolMode !== 'select') return
    const touch = e.touches[0]
    dragRef.current = {
      id: item.id,
      startMouseX: touch.clientX,
      startMouseY: touch.clientY,
      origX: item.pos_x,
      origY: item.pos_y,
    }
    const onMove = (ev) => {
      if (!dragRef.current) return
      ev.preventDefault()
      const t = ev.touches[0]
      const { id, startMouseX, startMouseY, origX, origY } = dragRef.current
      const newX = Math.max(0, origX + (t.clientX - startMouseX) / zoom)
      const newY = Math.max(0, origY + (t.clientY - startMouseY) / zoom)
      setItems(prev => prev.map(i => i.id === id ? { ...i, pos_x: newX, pos_y: newY } : i))
    }
    const onEnd = () => {
      if (!dragRef.current) return
      const { id } = dragRef.current
      setItems(prev => {
        const it = prev.find(i => i.id === id)
        if (it) supabase.from('moodboard_items').update({ pos_x: Math.round(it.pos_x), pos_y: Math.round(it.pos_y) }).eq('id', id).then(() => {})
        return prev
      })
      dragRef.current = null
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
    }
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onEnd)
  }, [toolMode, zoom])

  /* ─── Resize ─── */
  const handleResizeStart = useCallback((e, item) => {
    e.preventDefault(); e.stopPropagation()
    resizeRef.current = { id: item.id, startMouseX: e.clientX, origWidth: item.width }
    const onMove = (ev) => {
      if (!resizeRef.current) return
      const { id, startMouseX, origWidth } = resizeRef.current
      const newW = Math.max(80, Math.round(origWidth + (ev.clientX - startMouseX) / zoom))
      setItems(prev => prev.map(i => i.id === id ? { ...i, width: newW } : i))
    }
    const onUp = () => {
      if (!resizeRef.current) return
      const { id } = resizeRef.current
      setItems(prev => {
        const it = prev.find(i => i.id === id)
        if (it) supabase.from('moodboard_items').update({ width: it.width }).eq('id', id).then(() => {})
        return prev
      })
      resizeRef.current = null
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [zoom])

  /* ─── Draw mode (arrow / rect / circle) ─── */
  const handleCanvasMouseDown = useCallback((e) => {
    if (toolMode === 'select' || e.button !== 0) return
    e.preventDefault()
    const start = toCanvas(e.clientX, e.clientY)
    setDrawStart(start)
    setDrawPreview(start)
    const onMove = (ev) => {
      const cur = toCanvas(ev.clientX, ev.clientY)
      setDrawPreview(cur)
    }
    const onUp = async (ev) => {
      const end = toCanvas(ev.clientX, ev.clientY)
      const dx = end.x - start.x
      const dy = end.y - start.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist > 15 && selectedProject && myId) {
        const baseItem = {
          project_id: selectedProject.id,
          type: toolMode,
          pos_x: Math.round(Math.min(start.x, end.x)),
          pos_y: Math.round(Math.min(start.y, end.y)),
          stroke_color: strokeColor,
          created_by: myId,
        }
        if (toolMode === 'arrow') {
          Object.assign(baseItem, { end_x: Math.round(end.x), end_y: Math.round(end.y) })
          // Override pos_x/pos_y back to true start for arrow
          baseItem.pos_x = Math.round(start.x)
          baseItem.pos_y = Math.round(start.y)
        } else {
          Object.assign(baseItem, {
            width: Math.max(20, Math.abs(dx)),
            height: Math.max(20, Math.abs(dy)),
          })
        }
        const { data } = await supabase.from('moodboard_items').insert(baseItem).select().single()
        if (data) setItems(prev => [...prev, data])
      }
      setDrawStart(null); setDrawPreview(null)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [toolMode, toCanvas, selectedProject, myId, strokeColor])

  /* ─── Touch Draw (mobile) ─── */
  const handleCanvasTouchStart = useCallback((e) => {
    if (toolMode === 'select') return
    e.preventDefault()
    const touch = e.touches[0]
    const start = toCanvas(touch.clientX, touch.clientY)
    setDrawStart(start)
    setDrawPreview(start)
    const onMove = (ev) => {
      const t = ev.touches[0]
      const cur = toCanvas(t.clientX, t.clientY)
      setDrawPreview(cur)
    }
    const onEnd = async (ev) => {
      const t = ev.changedTouches[0]
      const end = toCanvas(t.clientX, t.clientY)
      const dx = end.x - start.x
      const dy = end.y - start.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist > 15 && selectedProject && myId) {
        const baseItem = {
          project_id: selectedProject.id,
          type: toolMode,
          pos_x: Math.round(Math.min(start.x, end.x)),
          pos_y: Math.round(Math.min(start.y, end.y)),
          stroke_color: strokeColor,
          created_by: myId,
        }
        if (toolMode === 'arrow') {
          Object.assign(baseItem, { end_x: Math.round(end.x), end_y: Math.round(end.y) })
          baseItem.pos_x = Math.round(start.x)
          baseItem.pos_y = Math.round(start.y)
        } else {
          Object.assign(baseItem, {
            width: Math.max(20, Math.abs(dx)),
            height: Math.max(20, Math.abs(dy)),
          })
        }
        const { data } = await supabase.from('moodboard_items').insert(baseItem).select().single()
        if (data) setItems(prev => [...prev, data])
      }
      setDrawStart(null); setDrawPreview(null)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
    }
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onEnd)
  }, [toolMode, toCanvas, selectedProject, myId, strokeColor])

  useEffect(() => () => {
    window.removeEventListener('mousemove', () => {})
    window.removeEventListener('mouseup', () => {})
  }, [])

  /* ─── Multi upload ─── */
  const handleImageFiles = async (e) => {
    const files = Array.from(e.target.files)
    if (!files.length || !selectedProject || !myId) return
    setUploading(files.length)
    let baseX = 120 + Math.floor(Math.random() * 200)
    let baseY = 120 + Math.floor(Math.random() * 200)
    await Promise.all(files.map(async (file, idx) => {
      const ext = file.name.split('.').pop()
      const path = `${myId}/${Date.now()}-${idx}.${ext}`
      const { error } = await supabase.storage.from('moodboard').upload(path, file)
      if (error) { console.error(error); return }
      const { data: urlData } = supabase.storage.from('moodboard').getPublicUrl(path)
      const { data } = await supabase.from('moodboard_items').insert({
        project_id: selectedProject.id, type: 'image',
        image_url: urlData.publicUrl,
        pos_x: baseX + idx * 30, pos_y: baseY + idx * 30,
        width: 260, created_by: myId,
      }).select().single()
      if (data) setItems(prev => [...prev, data])
    }))
    setUploading(0)
    e.target.value = ''
  }

  /* ─── Note ─── */
  const handleAddNote = async (testo, colore) => {
    setShowNoteModal(false)
    if (!selectedProject || !myId) return
    const { data } = await supabase.from('moodboard_items').insert({
      project_id: selectedProject.id, type: 'note', content: testo, color: colore,
      pos_x: 80 + Math.floor(Math.random() * 200), pos_y: 80 + Math.floor(Math.random() * 200),
      width: 220, created_by: myId,
    }).select().single()
    if (data) setItems(prev => [...prev, data])
  }

  /* ─── Delete ─── */
  const handleDelete = useCallback(async (id) => {
    const item = items.find(i => i.id === id)
    if (item) undoStackRef.current.push(item)
    await supabase.from('moodboard_items').delete().eq('id', id)
    setItems(prev => prev.filter(i => i.id !== id))
    setSelectedId(prev => prev === id ? null : prev)
  }, [items])

  /* ─── Keyboard shortcuts ─── */
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA'

      /* Ctrl/Cmd+Z — undo last delete */
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        const last = undoStackRef.current.pop()
        if (!last) return
        // eslint-disable-next-line no-unused-vars
        const { id, created_at, ...rest } = last
        supabase.from('moodboard_items').insert(rest).select().single()
          .then(({ data }) => { if (data) setItems(prev => [...prev, data]) })
        return
      }
      /* Delete / Backspace — remove selected item */
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !isTyping) {
        handleDelete(selectedId)
        return
      }
      /* Escape — deselect */
      if (e.key === 'Escape') setSelectedId(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedId, handleDelete])

  const zoomIn = () => setZoom(z => Math.min(2, parseFloat((z + 0.1).toFixed(1))))
  const zoomOut = () => setZoom(z => Math.max(0.25, parseFloat((z - 0.1).toFixed(1))))
  const zoomReset = () => setZoom(0.75)

  const toolBtnCls = (active) =>
    `flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
      active
        ? 'bg-violet-600 border-violet-600 text-white'
        : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white'
    }`

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)] -mt-3 -ml-3 sm:-mt-5 sm:-ml-5 lg:-mt-8 lg:-ml-8 overflow-hidden">

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 border-b border-gray-800 flex-shrink-0 flex-wrap gap-y-2">

        {/* Project selector */}
        <div className="relative">
          <button onClick={e => { e.stopPropagation(); setShowProjectPicker(v => !v) }}
            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white transition-colors">
            {selectedProject && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: selectedProject.colore }} />}
            <span className="max-w-[140px] truncate">{selectedProject?.titolo ?? 'Progetto'}</span>
            <ChevronDown size={13} className="text-gray-400" />
          </button>
          {showProjectPicker && (
            <div className="absolute top-full left-0 mt-1.5 w-52 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-20 overflow-hidden"
              onClick={e => e.stopPropagation()}>
              {progetti.map(p => (
                <button key={p.id} onClick={() => { setSelectedProject(p); setShowProjectPicker(false) }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-gray-700 text-gray-300 transition-colors">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.colore }} />
                  <span className="truncate">{p.titolo}</span>
                  {selectedProject?.id === p.id && <Check size={12} className="ml-auto text-violet-400" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="h-5 w-px bg-gray-700" />

        {/* Tool buttons */}
        <button onClick={() => setToolMode('select')} className={toolBtnCls(toolMode === 'select')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 4-5 2-2 5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
          <span className="hidden sm:inline">Seleziona</span>
        </button>
        <button onClick={() => setShowNoteModal(true)} disabled={!selectedProject} className={toolBtnCls(false) + ' disabled:opacity-40'}>
          <StickyNote size={14} />
          <span className="hidden sm:inline">Nota</span>
        </button>
        <button onClick={() => fileInputRef.current?.click()} disabled={!selectedProject || uploading > 0} className={toolBtnCls(false) + ' disabled:opacity-40'}>
          {uploading > 0
            ? <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            : <Image size={14} />}
          <span className="hidden sm:inline">{uploading > 0 ? `${uploading}...` : 'Foto'}</span>
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageFiles} className="hidden" />

        <div className="h-5 w-px bg-gray-700" />

        {/* Shape tools */}
        <button onClick={() => setToolMode(toolMode === 'arrow' ? 'select' : 'arrow')} disabled={!selectedProject} className={toolBtnCls(toolMode === 'arrow') + ' disabled:opacity-40'}>
          <ArrowUpRight size={14} /> <span className="hidden sm:inline">Freccia</span>
        </button>
        <button onClick={() => setToolMode(toolMode === 'rect' ? 'select' : 'rect')} disabled={!selectedProject} className={toolBtnCls(toolMode === 'rect') + ' disabled:opacity-40'}>
          <Square size={13} /> <span className="hidden sm:inline">Rettang.</span>
        </button>
        <button onClick={() => setToolMode(toolMode === 'circle' ? 'select' : 'circle')} disabled={!selectedProject} className={toolBtnCls(toolMode === 'circle') + ' disabled:opacity-40'}>
          <Circle size={13} /> <span className="hidden sm:inline">Cerchio</span>
        </button>

        {/* Stroke color (for shapes/arrows) */}
        {toolMode !== 'select' && toolMode !== 'note' && (
          <div className="flex items-center gap-1.5 ml-1">
            {STROKE_COLORS.map(c => (
              <button key={c} onClick={() => setStrokeColor(c)}
                className="w-5 h-5 rounded-full border-2 transition-transform hover:scale-110"
                style={{ backgroundColor: c, borderColor: strokeColor === c ? 'white' : 'transparent' }} />
            ))}
          </div>
        )}

        {/* Zoom */}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={zoomOut} className="w-8 h-8 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white flex items-center justify-center transition-colors" title="Rimpicciolisci"><ZoomOut size={15} /></button>
          <button onClick={zoomReset} className="text-xs text-gray-400 hover:text-white w-12 text-center transition-colors" title="Reset zoom">{Math.round(zoom * 100)}%</button>
          <button onClick={zoomIn} className="w-8 h-8 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white flex items-center justify-center transition-colors" title="Ingrandisci"><ZoomIn size={15} /></button>
        </div>
      </div>

      {/* Tool hint */}
      {toolMode !== 'select' && (
        <div className="bg-violet-900/30 border-b border-violet-800/30 px-4 py-1.5 text-xs text-violet-300 flex items-center gap-2 flex-shrink-0">
          <span>🖊</span>
          {toolMode === 'arrow' && 'Tocca/trascina sulla scrivania per disegnare una freccia'}
          {toolMode === 'rect' && 'Tocca/trascina per disegnare un rettangolo'}
          {toolMode === 'circle' && 'Tocca/trascina per disegnare un cerchio'}
          <button onClick={() => setToolMode('select')} className="ml-auto flex items-center gap-1 hover:text-white transition-colors"><X size={12} /> Esci</button>
        </div>
      )}

      {/* Canvas wrapper */}
      <div ref={canvasWrapRef} className="flex-1 overflow-auto bg-gray-950 relative"
        style={{
          backgroundImage: 'radial-gradient(circle, #374151 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          cursor: toolMode !== 'select' ? 'crosshair' : 'default',
        }}
        onClick={() => { setShowProjectPicker(false); setSelectedId(null) }}
        onMouseDown={handleCanvasMouseDown}
        onTouchStart={handleCanvasTouchStart}
      >
        {!selectedProject && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center"><div className="text-5xl mb-4">🎨</div>
              <p className="text-gray-500 text-sm">Seleziona un progetto per iniziare</p></div>
          </div>
        )}
        {selectedProject && items.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center"><div className="text-5xl mb-4">🖼️</div>
              <p className="text-gray-500 text-sm">Aggiungi elementi alla scrivania</p>
              <p className="text-gray-600 text-xs mt-1">I tuoi colleghi li vedranno in tempo reale</p></div>
          </div>
        )}

        {/* Canvas inner */}
        <div style={{ width: CANVAS_W * zoom, height: CANVAS_H * zoom, position: 'relative', flexShrink: 0 }}>

          {/* Draw preview */}
          {drawStart && drawPreview && toolMode === 'arrow' && (() => {
            const x1 = drawStart.x * zoom, y1 = drawStart.y * zoom
            const x2 = drawPreview.x * zoom, y2 = drawPreview.y * zoom
            const minX = Math.min(x1, x2) - 20, minY = Math.min(y1, y2) - 20
            const w = Math.abs(x2 - x1) + 40, h = Math.abs(y2 - y1) + 40
            return (
              <div style={{ position: 'absolute', left: minX, top: minY, width: w, height: h, pointerEvents: 'none' }}>
                <svg width={w} height={h} style={{ overflow: 'visible' }}>
                  <defs><marker id="prev-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill={strokeColor} /></marker></defs>
                  <line x1={x1 - minX} y1={y1 - minY} x2={x2 - minX} y2={y2 - minY}
                    stroke={strokeColor} strokeWidth={2} markerEnd="url(#prev-arrow)" strokeDasharray="6,3" opacity={0.7} />
                </svg>
              </div>
            )
          })()}
          {drawStart && drawPreview && (toolMode === 'rect' || toolMode === 'circle') && (
            <div style={{
              position: 'absolute',
              left: Math.min(drawStart.x, drawPreview.x) * zoom,
              top: Math.min(drawStart.y, drawPreview.y) * zoom,
              width: Math.abs(drawPreview.x - drawStart.x) * zoom,
              height: Math.abs(drawPreview.y - drawStart.y) * zoom,
              border: `2px dashed ${strokeColor}`,
              borderRadius: toolMode === 'circle' ? '50%' : '6px',
              opacity: 0.6,
              pointerEvents: 'none',
              backgroundColor: `${strokeColor}10`,
            }} />
          )}

          {/* Items */}
          {items.map(item => {
            const common = {
              key: item.id, item,
              isOwner: item.created_by === myId,
              hovered: hoveredId === item.id,
              selected: selectedId === item.id,
              onMouseEnter: () => setHoveredId(item.id),
              onMouseLeave: () => setHoveredId(null),
              onMouseDown: handleMouseDownItem,
              onTouchStart: handleTouchStartItem,
              onDelete: handleDelete,
              onSelect: setSelectedId,
              zoom,
            }
            if (item.type === 'image') return <ImageItem {...common} onResizeStart={handleResizeStart} />
            if (item.type === 'note') return <NoteItem {...common} />
            if (item.type === 'arrow') return <ArrowItem {...common} />
            if (item.type === 'rect' || item.type === 'circle') return <ShapeItem {...common} />
            return null
          })}
        </div>
      </div>

      {showNoteModal && <NoteModal onClose={() => setShowNoteModal(false)} onConfirm={handleAddNote} />}
    </div>
  )
}

export default Moodboard
